import { useEffect, useState } from "react";
import type { PaletteMode } from "@mui/material";
import type { View } from "./types";
import Layout from "./components/Layout";
import AuthGuard from "./AuthGuard";
import TenantGuard from "./TenantGuard";
import Dashboard from "./views/Dashboard";
import Companies from "./views/Companies";

const listViews: View[] = ["dashboard", "companies"];

interface Route {
  view: View;
  id?: number;
}

function parseHash(): Route {
  const raw = window.location.hash.replace(/^#/, "");
  const slash = raw.indexOf("/");
  if (slash !== -1) {
    const viewPart = raw.slice(0, slash) as View;
    const idPart = parseInt(raw.slice(slash + 1), 10);
    if (!isNaN(idPart)) return { view: viewPart, id: idPart };
  }
  const view = raw as View;
  return { view: listViews.includes(view) ? view : "dashboard" };
}

interface Props {
  themeMode: PaletteMode;
  onToggleThemeMode: () => void;
}

export default function App({ themeMode, onToggleThemeMode }: Props) {
  const [route, setRoute] = useState<Route>(parseHash);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const navigate = (v: View, id?: number) => {
    window.location.hash = id != null ? `${v}/${id}` : v;
    setRoute({ view: v, id });
  };

  return (
    <AuthGuard>
      <TenantGuard>
        <Layout
          view={route.view}
          onNavigate={navigate}
          themeMode={themeMode}
          onToggleThemeMode={onToggleThemeMode}
        >
          {route.view === "dashboard" && <Dashboard />}
          {route.view === "companies" && <Companies />}
        </Layout>
      </TenantGuard>
    </AuthGuard>
  );
}
